import { createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";

import { getLang } from "containers/authentication/redux/selector";
import { prepareListSchemaForFrontend } from "containers/lists/redux/utils";
import { IQuery } from "containers/lists/utils";
import { showNotificationActionCreator } from "containers/notifications/actionCreator";
import { getSuccessNotificationMessage } from "lang/utils";
import { LANG_ACTIONS, SUB_CATEGORIES } from "model/application/Lang";
import { IListSchema } from "model/entities/List";
import {
  ajaxRequestAction,
  ajaxSuccessAction,
} from "redux/actionCreators/ajaxActionCreator";
import {
  extractDataAndCheckErrorStatus,
  IGenericQueryToSendToBackend,
  treatErrorNotification,
} from "redux/actions/appActions";
import IRootState from "redux/store/model";

import * as lang from "../../../lang";
import * as notificationLevels from "../../notifications/actionLevels";
import * as notificationTypes from "../../notifications/actionTypes";
import { ITerritory, ITerritoryBE } from "../model";
import {
  formatTerritoryForBE,
  formatTerritoryForFE,
} from "../utils/formatTerritory";
import {
  createTerritoriesApiCall,
  deleteTerritoriesApiCall,
  fetchTerritoriesApiCall,
  fetchTerritoriesSchemaApiCall,
  TTerritoryOwnerUnassignLink,
  unassignTerritoryOwnersApiCall,
  updateTerritoriesApiCall,
  updateTerritoriesSchemaApiCall,
} from "./api";

const currentLang = (getState: () => IRootState) => lang[getLang(getState())];
const territorySuccess = (action: LANG_ACTIONS, count: number) => ({
  action,
  count,
});
export type TFetchTerritories = {
  query?: IGenericQueryToSendToBackend<ITerritoryBE & Record<string, unknown>>;
  frontendFilters?: IQuery;
};

const runTerritoryRequest = async <T>(
  { dispatch, getState, rejectWithValue }: any,
  errorName: string,
  request: () => Promise<T>,
  successCount?: { action: LANG_ACTIONS; count: number },
) => {
  const currLang = currentLang(getState);
  try {
    dispatch(ajaxRequestAction());
    const result = await request();
    dispatch(ajaxSuccessAction());
    if (successCount) {
      dispatch(
        showNotificationActionCreator(
          notificationTypes.NOTIFICATION_SUCCESS,
          notificationLevels.NOTIFICATION_LEVEL_SUCCESS,
          getSuccessNotificationMessage(
            currLang,
            successCount.action,
            SUB_CATEGORIES.TERRITORY,
            successCount.count,
            true,
          ),
        ),
      );
    }
    return result;
  } catch (error) {
    treatErrorNotification(dispatch, errorName, error, null, currLang);
    return rejectWithValue(error) as T;
  }
};

export const fetchTerritories = createAsyncThunk(
  "territories/fetchTerritories",
  (arg: TFetchTerritories | undefined, thunkApi) => {
    const query = arg?.query ?? { filters: {} };
    return runTerritoryRequest(thunkApi, "FetchTerritoriesError", async () => {
      const data = extractDataAndCheckErrorStatus<{
        territories: ITerritoryBE[];
        item_count: number;
        has_more: boolean;
      }>(
        await fetchTerritoriesApiCall({
          ...query,
          offset: query.offset ?? 0,
        }),
      );
      return {
        territories: _.compact(_.map(data.territories, formatTerritoryForFE)),
        item_count: data.item_count,
        has_more: data.has_more ?? false,
        frontendFilters: arg?.frontendFilters ?? {},
      };
    });
  },
);

export const fetchTerritoriesSchema = createAsyncThunk(
  "territories/fetchTerritoriesSchema",
  (_arg: void, thunkApi) =>
    runTerritoryRequest(thunkApi, "FetchTerritoriesSchemaError", async () => {
      const data = extractDataAndCheckErrorStatus<{ schema: IListSchema[] }>(
        await fetchTerritoriesSchemaApiCall(),
      );
      return prepareListSchemaForFrontend(data.schema as any);
    }),
);

export const createTerritories = createAsyncThunk(
  "territories/createTerritories",
  (territories: ITerritory[], thunkApi) =>
    runTerritoryRequest(
      thunkApi,
      "CreateTerritoriesError",
      async () => {
        extractDataAndCheckErrorStatus(
          await createTerritoriesApiCall(
            _.compact(_.map(territories, formatTerritoryForBE)),
          ),
        );
        return territories;
      },
      territorySuccess(LANG_ACTIONS.CREATE, territories.length),
    ),
);

export const deleteTerritories = createAsyncThunk(
  "territories/deleteTerritories",
  (itemIds: string[], thunkApi) =>
    runTerritoryRequest(
      thunkApi,
      "DeleteTerritoriesError",
      async () => {
        extractDataAndCheckErrorStatus(await deleteTerritoriesApiCall(itemIds));
        return itemIds;
      },
      territorySuccess(LANG_ACTIONS.DELETE, itemIds.length),
    ),
);

export const editTerritories = createAsyncThunk(
  "territories/editTerritories",
  (territoryItems: ITerritoryBE[], thunkApi) =>
    runTerritoryRequest(
      thunkApi,
      "EditTerritoriesError",
      async () => {
        extractDataAndCheckErrorStatus(
          await updateTerritoriesApiCall(territoryItems as any),
        );
        return territoryItems;
      },
      territorySuccess(LANG_ACTIONS.EDIT, territoryItems.length),
    ),
);

export const editTerritoriesSchema = createAsyncThunk(
  "territories/editTerritoriesSchema",
  (schema: IListSchema[], thunkApi) =>
    runTerritoryRequest(
      thunkApi,
      "EditTerritoriesSchemaError",
      async () => {
        extractDataAndCheckErrorStatus(
          await updateTerritoriesSchemaApiCall(schema),
        );
        return schema;
      },
      territorySuccess(LANG_ACTIONS.EDIT, schema.length),
    ),
);

export type TUnassignTerritoryOwnersResult = {
  notFoundOwners: string[];
  notFoundTerritories: string[];
};

export const unassignTerritoryOwners = createAsyncThunk(
  "territories/unassignTerritoryOwners",
  async (links: TTerritoryOwnerUnassignLink[], thunkApi) => {
    const result = await runTerritoryRequest(
      thunkApi,
      "UnassignTerritoryOwnersError",
      async () => {
        const data = extractDataAndCheckErrorStatus<{
          notFoundOwners?: string[];
          notFoundTerritories?: string[];
        }>(await unassignTerritoryOwnersApiCall(links));
        await thunkApi.dispatch(
          fetchTerritories({ query: { filters: {}, with_count: true } }),
        );
        const notFoundOwners = data?.notFoundOwners ?? [];
        const notFoundTerritories = data?.notFoundTerritories ?? [];
        if (!notFoundOwners.length && !notFoundTerritories.length) {
          return null;
        }
        return { notFoundOwners, notFoundTerritories };
      },
      territorySuccess(LANG_ACTIONS.UNASSIGN, links.length),
    );
    return result;
  },
);
